import { useMemo, useState } from "react";
import {
  Factory,
  AlertTriangle,
  Loader2,
  ClipboardX,
  Cylinder,
  Package,
  X,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Mail,
  Table2,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/api";

type PoStatus = "covered" | "partial" | "missing";

type PlanLine = {
  id: string;
  sku: string;
  description: string | null;
  warehouse: string;
  month: string;
  qty: string | number | null;
  containers: string | number | null;
  unit: string;
  po_status: string;
  po_refs: string | null;
  etd: string | null;
  booking: string | null;
  shipping_agent: string | null;
  notes: string | null;
  flags: unknown;
  sort_order: number | null;
  source_type: string | null;
  source_label: string | null;
  source_url: string | null;
};

type PlanAlert = {
  severity?: string;
  type?: string;
  sku?: string;
  message?: string;
  dismissed?: boolean;
};
type PlanException = {
  sku?: string;
  warehouse?: string;
  month?: string;
  issue?: string;
  detail?: string;
};

type PlanRun = {
  id: string;
  run_label: string | null;
  months: string[];
  status: string;
  summary: Record<string, unknown>;
  alerts: unknown;
  exceptions: unknown;
  created_at: string;
};

type PlanResponse = { run: PlanRun | null; lines: PlanLine[] };

const WAREHOUSE_ORDER = ["Riverside", "Elkhart", "Savannah"];

// Certainty tones — the cell color answers "how solid is this number, and
// what's left to do": PO sent (green) → PO exists but mismatched (amber) →
// Isaac asked, PO owed (blue) → only Sruli/Rivky so far, needs validation + PO (orange).
type Tone = "green" | "amber" | "blue" | "orange";

const TONE_STYLE: Record<Tone, { cell: string; dot: string; label: string }> = {
  green: { cell: "bg-emerald-50 hover:bg-emerald-100", dot: "bg-emerald-500", label: "PO sent" },
  amber: { cell: "bg-amber-50 hover:bg-amber-100", dot: "bg-amber-500", label: "Partial / mismatch" },
  blue: { cell: "bg-sky-50 hover:bg-sky-100", dot: "bg-sky-500", label: "Isaac request — no PO yet" },
  orange: { cell: "bg-orange-50 hover:bg-orange-100", dot: "bg-orange-400", label: "Sruli/Rivky request — no PO yet" },
};

const STATUS_BADGE: Record<PoStatus, { badge: string; label: string }> = {
  covered: { badge: "bg-emerald-100 text-emerald-700", label: "PO covered" },
  partial: { badge: "bg-amber-100 text-amber-700", label: "Partial / mismatch" },
  missing: { badge: "bg-red-100 text-red-700", label: "No PO" },
};

function toStatus(s: string): PoStatus {
  return s === "covered" || s === "partial" ? s : "missing";
}

// Isaac's asks (sheet or his own email) carry the same authority; anything
// whose only origin is a Sruli/Rivky email is softer until Isaac/PO confirms.
function isIsaacOrigin(l: PlanLine): boolean {
  if (l.source_type === "sheet") return true;
  return (l.source_label || "").toLowerCase().startsWith("isaac");
}

function lineTone(l: PlanLine): Tone {
  const s = toStatus(l.po_status);
  if (s === "covered") return "green";
  if (s === "partial") return "amber";
  return isIsaacOrigin(l) ? "blue" : "orange";
}

const TONE_RANK: Record<Tone, number> = { orange: 0, blue: 1, amber: 2, green: 3 };

function hasAgingFlag(l: PlanLine): boolean {
  return Array.isArray(l.flags) && (l.flags as unknown[]).includes("aging_no_po");
}

function toNum(v: string | number | null): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function monthLabel(m: string): string {
  const parts = m.split("-");
  if (parts.length < 2) return m;
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function monthShort(m: string): string {
  const parts = m.split("-");
  if (parts.length < 2) return m;
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
  return `${d.toLocaleDateString("en-US", { month: "short" })} '${parts[0].slice(2)}`;
}

function formatDate(s: string | null): string {
  if (!s) return "TBD";
  try {
    return new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "TBD";
  }
}

// Tanks are cylinder lines; everything else (pcs, containers of covers, cages,
// torches, heaters…) renders in the Accessories & Appliances section below.
function isTank(line: PlanLine): boolean {
  return line.unit === "cylinders";
}

type SkuEntry = {
  sku: string;
  description: string | null;
  sortOrder: number;
  cells: Map<string, PlanLine[]>; // key: `${warehouse}|${month}` — a cell can hold several shipments
};

function buildSkuEntries(lines: PlanLine[]): SkuEntry[] {
  const bySku = new Map<string, SkuEntry>();
  for (const line of lines) {
    let entry = bySku.get(line.sku);
    if (!entry) {
      entry = { sku: line.sku, description: null, sortOrder: 9999, cells: new Map() };
      bySku.set(line.sku, entry);
    }
    if (!entry.description && line.description) entry.description = line.description;
    if (line.sort_order !== null && line.sort_order < entry.sortOrder) entry.sortOrder = line.sort_order;
    const key = `${line.warehouse}|${line.month}`;
    const cell = entry.cells.get(key);
    if (cell) cell.push(line);
    else entry.cells.set(key, [line]);
  }
  return Array.from(bySku.values()).sort((a, b) => a.sortOrder - b.sortOrder);
}

type SelectedCell = {
  sku: string;
  description: string | null;
  warehouse: string;
  month: string;
  lines: PlanLine[];
};

export function ProductionPlanPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<SelectedCell | null>(null);
  const [openAlerts, setOpenAlerts] = useState<Set<number>>(new Set());
  const [locallyDismissed, setLocallyDismissed] = useState<Set<number>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ["production-plan"],
    queryFn: async (): Promise<PlanResponse> => {
      const res = await api(`/api/production-plan`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as PlanResponse;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const run = data?.run ?? null;
  const lines = useMemo(() => data?.lines ?? [], [data]);

  const months: string[] = useMemo(() => {
    if (run?.months && run.months.length > 0) return run.months;
    return Array.from(new Set(lines.map((l) => l.month))).sort();
  }, [run, lines]);

  const alerts: PlanAlert[] = useMemo(
    () => (Array.isArray(run?.alerts) ? (run.alerts as PlanAlert[]) : []),
    [run],
  );
  const visibleAlerts = useMemo(
    () =>
      alerts
        .map((a, i) => ({ a, i }))
        .filter(({ a, i }) => !a.dismissed && !locallyDismissed.has(i)),
    [alerts, locallyDismissed],
  );
  const exceptions: PlanException[] = useMemo(
    () => (Array.isArray(run?.exceptions) ? (run.exceptions as PlanException[]) : []),
    [run],
  );

  const warehouses = useMemo(() => {
    const present = Array.from(new Set(lines.map((l) => l.warehouse)));
    const known = WAREHOUSE_ORDER.filter((w) => present.includes(w));
    const extra = present.filter((w) => !WAREHOUSE_ORDER.includes(w)).sort();
    return [...known, ...extra];
  }, [lines]);

  const tankEntries = useMemo(() => buildSkuEntries(lines.filter(isTank)), [lines]);
  const accessoryEntries = useMemo(() => buildSkuEntries(lines.filter((l) => !isTank(l))), [lines]);

  function toggleAlert(i: number) {
    setOpenAlerts((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  async function dismissAlert(i: number) {
    // Optimistic hide; the server persists it on the active run. A future
    // skill run writes fresh alerts, so still-true topics re-raise there.
    setLocallyDismissed((prev) => new Set(prev).add(i));
    try {
      await api(`/api/production-plan/dismiss-alert`, {
        method: "POST",
        body: JSON.stringify({ index: i }),
      });
      queryClient.invalidateQueries({ queryKey: ["production-plan"] });
    } catch {
      setLocallyDismissed((prev) => {
        const next = new Set(prev);
        next.delete(i);
        return next;
      });
    }
  }

  function renderSection(title: string, icon: React.ReactNode, entries: SkuEntry[]) {
    if (entries.length === 0) return null;
    return (
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b flex items-center gap-2">
          {icon}
          <span className="text-sm font-semibold text-foreground">{title}</span>
          <span className="ml-auto text-xs text-muted-foreground">{entries.length} SKUs</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 border-b">
                <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground w-52" rowSpan={2}>
                  SKU
                </th>
                {warehouses.map((wh) => (
                  <th
                    key={wh}
                    colSpan={months.length}
                    className="text-center px-2 py-2 text-xs font-bold text-foreground border-l border-border"
                  >
                    {wh}
                  </th>
                ))}
              </tr>
              <tr className="bg-muted/30 border-b">
                {warehouses.flatMap((wh) =>
                  months.map((m, mi) => (
                    <th
                      key={`${wh}|${m}`}
                      className={`text-right px-2.5 py-1.5 text-[11px] font-semibold text-muted-foreground ${
                        mi === 0 ? "border-l border-border" : ""
                      }`}
                    >
                      {monthShort(m)}
                    </th>
                  )),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {entries.map((entry) => (
                <tr key={entry.sku}>
                  <td className="px-4 py-2.5 text-xs font-semibold text-foreground">
                    {entry.sku}
                    {entry.description && (
                      <div className="text-[10px] font-normal text-muted-foreground">{entry.description}</div>
                    )}
                  </td>
                  {warehouses.flatMap((wh) =>
                    months.map((m, mi) => {
                      const cellLines = entry.cells.get(`${wh}|${m}`);
                      const groupBorder = mi === 0 ? "border-l border-border" : "";
                      if (!cellLines || cellLines.length === 0)
                        return <td key={`${wh}|${m}`} className={`px-2.5 py-2.5 ${groupBorder}`} />;
                      // Cell takes the least-certain tone among its shipments,
                      // so a pending line can't hide behind a PO'd one.
                      const tone = cellLines
                        .map(lineTone)
                        .reduce((a, b) => (TONE_RANK[a] <= TONE_RANK[b] ? a : b));
                      const aging = cellLines.some(hasAgingFlag);
                      const qtySum = cellLines.reduce((acc, l) => acc + (toNum(l.qty) ?? 0), 0);
                      const hasQty = cellLines.some((l) => toNum(l.qty) !== null);
                      const contSum = cellLines.reduce((acc, l) => acc + (toNum(l.containers) ?? 0), 0);
                      const hasCont = cellLines.some((l) => toNum(l.containers) !== null);
                      return (
                        <td
                          key={`${wh}|${m}`}
                          onClick={() =>
                            setSelected({
                              sku: entry.sku,
                              description: entry.description,
                              warehouse: wh,
                              month: m,
                              lines: cellLines,
                            })
                          }
                          className={`px-2.5 py-2.5 text-right cursor-pointer transition-colors ${TONE_STYLE[tone].cell} ${groupBorder} ${
                            aging ? "ring-1 ring-inset ring-red-400" : ""
                          }`}
                          title="Click for shipment details"
                        >
                          <div className="flex items-center justify-end gap-1">
                            <span
                              className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                aging ? "bg-red-500" : TONE_STYLE[tone].dot
                              }`}
                            />
                            <span className="text-xs font-semibold text-foreground tabular-nums">
                              {hasQty ? qtySum.toLocaleString() : hasCont ? `${contSum} cont.` : "—"}
                            </span>
                          </div>
                          {hasQty && hasCont && (
                            <div className="text-[10px] text-muted-foreground">{contSum.toLocaleString()} cont.</div>
                          )}
                        </td>
                      );
                    }),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col h-full"
      style={{ fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" }}
    >
      {/* Header */}
      <div className="px-8 py-5 border-b bg-background flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center">
            <Factory className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-foreground">FlameKing Plan</h1>
            <p className="text-xs text-muted-foreground">
              Production expectations by warehouse and month, PO-checked
              {run ? ` · ${run.run_label || "latest run"} · updated ${formatDate(run.created_at)}` : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          {(Object.keys(TONE_STYLE) as Tone[]).map((t) => (
            <span key={t} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className={`inline-block w-2 h-2 rounded-full ${TONE_STYLE[t].dot}`} />
              {TONE_STYLE[t].label}
            </span>
          ))}
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
            Aging — chase
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-[#f8fafc] p-6 space-y-6">
        {isLoading && (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading plan…
          </div>
        )}

        {!isLoading && !run && (
          <div className="bg-card border border-dashed border-border rounded-xl px-6 py-16 text-center">
            <Factory className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm font-medium text-foreground">No production plan yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              This page fills in when Jarvis runs the production-expectations skill. The skill
              inserts a new plan run; this page always shows the latest.
            </p>
          </div>
        )}

        {/* Alerts — one line each, click to expand, X to dismiss (next scan re-raises if still true) */}
        {visibleAlerts.length > 0 && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-semibold text-foreground">Alerts</span>
              <span className="ml-auto text-xs text-muted-foreground">{visibleAlerts.length}</span>
            </div>
            <ul className="divide-y divide-border">
              {visibleAlerts.map(({ a, i }) => {
                const isOpen = openAlerts.has(i);
                return (
                  <li key={i}>
                    <div className="w-full px-5 py-2 flex items-center gap-2 text-xs hover:bg-muted/20 transition-colors">
                      <button
                        onClick={() => toggleAlert(i)}
                        className="flex items-center gap-2 min-w-0 flex-1 text-left"
                      >
                        {isOpen ? (
                          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                        )}
                        <span
                          className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                            a.severity === "warning" || a.severity === "red" ? "bg-amber-500" : "bg-sky-400"
                          }`}
                        />
                        <span className={`text-foreground min-w-0 flex-1 ${isOpen ? "" : "truncate"}`}>
                          {a.sku && <span className="font-semibold">{a.sku} — </span>}
                          {!isOpen && (a.message || a.type || "Alert")}
                          {isOpen && (a.type ? a.type.replace(/_/g, " ") : "Alert")}
                        </span>
                      </button>
                      <button
                        onClick={() => dismissAlert(i)}
                        title="Dismiss — the next scan re-raises it if still relevant"
                        aria-label="Dismiss alert"
                        className="p-1 rounded hover:bg-muted/50 text-muted-foreground flex-shrink-0"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {isOpen && (
                      <div className="px-5 pb-3 pl-[52px] text-xs text-foreground leading-relaxed">
                        {a.message || "—"}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Tanks — all warehouses side by side, Projected Shipments - Isaac style */}
        {renderSection("Tanks", <Cylinder className="w-4 h-4 text-muted-foreground" />, tankEntries)}

        {/* Accessories & Appliances */}
        {renderSection(
          "Accessories & Appliances",
          <Package className="w-4 h-4 text-muted-foreground" />,
          accessoryEntries,
        )}

        {/* PO exceptions */}
        {exceptions.length > 0 && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b flex items-center gap-2">
              <ClipboardX className="w-4 h-4 text-red-500" />
              <span className="text-sm font-semibold text-foreground">PO Exceptions</span>
              <span className="ml-auto text-xs text-muted-foreground">{exceptions.length}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/30 border-b">
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">SKU</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Warehouse</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Month</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Issue</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {exceptions.map((e, i) => (
                    <tr key={i} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3 text-xs font-semibold text-foreground">{e.sku || "—"}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{e.warehouse || "—"}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{e.month ? monthLabel(e.month) : "—"}</td>
                      <td className="px-4 py-3 text-xs font-medium text-foreground">{e.issue || "—"}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{e.detail || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Shipment popup — details for one SKU × warehouse × month cell */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <div className="absolute inset-0 bg-black/30" />
          <div
            className="relative bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b flex items-start justify-between gap-3 sticky top-0 bg-card">
              <div>
                <div className="text-sm font-semibold text-foreground">{selected.sku}</div>
                {selected.description && (
                  <div className="text-[11px] text-muted-foreground">{selected.description}</div>
                )}
                <div className="text-xs text-muted-foreground mt-1">
                  <span className="font-medium text-foreground">{selected.warehouse}</span> ·{" "}
                  {monthLabel(selected.month)} ·{" "}
                  {selected.lines.length === 1 ? "1 shipment line" : `${selected.lines.length} shipment lines`}
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground flex-shrink-0"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {selected.lines.map((l) => {
                const status = toStatus(l.po_status);
                const tone = lineTone(l);
                const qty = toNum(l.qty);
                const containers = toNum(l.containers);
                const isSheet = l.source_type === "sheet";
                return (
                  <div key={l.id} className="border border-border rounded-lg overflow-hidden">
                    <div className="px-4 py-2.5 bg-muted/20 flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-foreground tabular-nums">
                        {qty !== null ? `${qty.toLocaleString()} ${l.unit}` : ""}
                        {qty !== null && containers !== null ? " · " : ""}
                        {containers !== null ? `${containers.toLocaleString()} container${containers === 1 ? "" : "s"}` : ""}
                        {qty === null && containers === null ? "Quantity TBD" : ""}
                      </span>
                      <span className="flex items-center gap-1.5 flex-shrink-0">
                        <span
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE[status].badge}`}
                        >
                          {STATUS_BADGE[status].label}
                        </span>
                        {status === "missing" && (
                          <span className="text-[10px] text-muted-foreground">{TONE_STYLE[tone].label}</span>
                        )}
                      </span>
                    </div>
                    {/* Demand source — sheet tab vs email, with a link to the original */}
                    {(l.source_label || l.source_type) && (
                      <div className="px-4 py-2 border-t border-border/60 bg-muted/10 flex items-center gap-2 text-xs">
                        <span
                          className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                            isSheet ? "bg-blue-100 text-blue-700" : "bg-violet-100 text-violet-700"
                          }`}
                        >
                          {isSheet ? <Table2 className="w-3 h-3" /> : <Mail className="w-3 h-3" />}
                          {isSheet ? "Sheet" : "Email"}
                        </span>
                        <span className="text-foreground min-w-0 truncate">{l.source_label || "—"}</span>
                        {l.source_url && (
                          <a
                            href={l.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="ml-auto flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium flex-shrink-0"
                          >
                            Open <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    )}
                    <dl className="px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs border-t border-border/60">
                      <div>
                        <dt className="text-[10px] font-semibold text-muted-foreground uppercase">PO(s)</dt>
                        <dd className="font-mono text-foreground mt-0.5">{l.po_refs || "????"}</dd>
                      </div>
                      <div>
                        <dt className="text-[10px] font-semibold text-muted-foreground uppercase">ETD</dt>
                        <dd className="text-blue-600 font-medium mt-0.5">{formatDate(l.etd)}</dd>
                      </div>
                      <div>
                        <dt className="text-[10px] font-semibold text-muted-foreground uppercase">Booking</dt>
                        <dd className="text-foreground mt-0.5">{l.booking || "—"}</dd>
                      </div>
                      <div>
                        <dt className="text-[10px] font-semibold text-muted-foreground uppercase">Shipping agent</dt>
                        <dd className="text-foreground mt-0.5">{l.shipping_agent || "—"}</dd>
                      </div>
                      {l.notes && (
                        <div className="col-span-2">
                          <dt className="text-[10px] font-semibold text-muted-foreground uppercase">Notes</dt>
                          <dd className="text-muted-foreground mt-0.5 leading-relaxed">{l.notes}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

(ProductionPlanPage as unknown as { path: string }).path = "/production-plan";
